import {
  Add as AddIcon,
  Search as SearchIcon,
  StarRate as StarRateIcon
} from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputAdornment,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';

import { CounterpartyDropDown, Invoice } from 'openapi';
import { Company } from 'openapi';

import { useCompanies } from 'context/CompanyContext';
import { useTranslations } from 'context/TranslationContext';

import {
  CONFIDENCE_STATUS_COLORS,
  getConfidenceFieldSx
} from 'utils/constants/confidenceStatusColors';
import { COUNTERPARTY_SEARCH_MIN_CHARACTERS } from 'utils/constants/invoices';
import { InvoiceVerificationFieldName } from 'utils/constants/invoiceVerificationFieldNames';
import { getFieldConfidenceUi } from 'utils/helpers/confidenceHelpers';
import { getCounterPartySearchOptionLabel } from 'utils/helpers/invoiceHelpers';

import { verificationFormItemNoHeight } from 'styles/pages/InvoiceVerificationStyle';

import { useCounterpartySearch } from './hooks/useCounterpartySearch';

interface CounterpartyAutocompleteProps {
  currentInvoice: Invoice;
  watchedCounterpartyName: string;
  showCancel: boolean;
  showCreateCard: boolean;
  onCancel: () => void;
  onCreateClick: (searchValue: string) => void;
  onSearchInputChange?: () => void;
}

export const CounterpartyAutocomplete = ({
  currentInvoice,
  watchedCounterpartyName,
  showCancel,
  showCreateCard,
  onCancel,
  onCreateClick,
  onSearchInputChange
}: CounterpartyAutocompleteProps) => {
  const { translate } = useTranslations();
  const { control } = useFormContext();
  const companyName = useCompanies().companiesList.find(
    (company: Company) => company.id === currentInvoice.companyId
  )?.name;

  const {
    open,
    setOpen,
    options,
    isLoading,
    setIsLoading,
    searchParams,
    setSearchParams,
    notEnoughCharactersToSearch,
    handleSelect
  } = useCounterpartySearch(currentInvoice);

  return (
    <Box>
      <Controller
        name={InvoiceVerificationFieldName.CounterpartyId}
        control={control}
        rules={{
          validate: (value) =>
            Boolean(value) || Boolean(watchedCounterpartyName)
              ? true
              : translate('errors.required')
        }}
        render={({ field, fieldState: { error, isDirty } }) => {
          const { isLow, isMedium, confidenceMessage } = getFieldConfidenceUi(
            currentInvoice,
            InvoiceVerificationFieldName.CounterpartyId,
            translate,
            isDirty
          );
          const isEmpty = !field.value && !watchedCounterpartyName;
          const hasRequiredError = !!error || isEmpty;
          const confidenceTone = hasRequiredError
            ? null
            : isLow
              ? 'lowConfidence'
              : isMedium
                ? 'needsReview'
                : null;
          const showError = hasRequiredError || isLow;
          const isWarning = confidenceTone === 'needsReview';

          return (
            <FormControl
              sx={
                confidenceTone
                  ? getConfidenceFieldSx(
                      verificationFormItemNoHeight,
                      confidenceTone
                    )
                  : verificationFormItemNoHeight
              }
              fullWidth
              error={showError}
              color={isWarning ? 'warning' : undefined}
            >
              <Autocomplete
                open={
                  open &&
                  !isLoading &&
                  (notEnoughCharactersToSearch || options.length > 0)
                }
                disableClearable
                blurOnSelect
                clearOnBlur={false}
                forcePopupIcon={false}
                onChange={(_event, selectedOption) => {
                  handleSelect(selectedOption as CounterpartyDropDown);
                }}
                onInputChange={(_event, value, reason) => {
                  setSearchParams(value);
                  setIsLoading(true);
                  if (reason === 'input' && showCreateCard) {
                    onSearchInputChange?.();
                  }
                }}
                onOpen={() => {
                  setOpen(true);
                }}
                onClose={(_event, reason) => {
                  if (reason === 'escape' || reason === 'blur') {
                    setOpen(false);
                  }
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionLabel={(option) =>
                  getCounterPartySearchOptionLabel(
                    option as CounterpartyDropDown
                  )
                }
                options={options}
                noOptionsText={
                  notEnoughCharactersToSearch &&
                  translate('messages.searchRequiredCharacters', {
                    numberOfCharacters: String(
                      COUNTERPARTY_SEARCH_MIN_CHARACTERS
                    )
                  })
                }
                loading={notEnoughCharactersToSearch ? false : isLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    {...field}
                    error={showError}
                    color={isWarning ? 'warning' : undefined}
                    sx={
                      confidenceTone
                        ? getConfidenceFieldSx({}, confidenceTone)
                        : undefined
                    }
                    label={translate('labels.counterparty')}
                    helperText={
                      hasRequiredError
                        ? translate('errors.required')
                        : (confidenceMessage?.text ?? '')
                    }
                    FormHelperTextProps={{
                      sx: {
                        color: confidenceTone
                          ? CONFIDENCE_STATUS_COLORS[confidenceTone].text
                          : undefined
                      }
                    }}
                    onChange={(event) => {
                      setSearchParams(event.target.value);
                    }}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment sx={{ ml: 1 }} position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          {isLoading ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                        </InputAdornment>
                      )
                    }}
                  />
                )}
                renderOption={(props, option) => {
                  const { key, ...rest } = props;
                  return (
                    <Tooltip
                      key={key}
                      title={
                        (option as CounterpartyDropDown).isCompanyCounterparty
                          ? `${translate('messages.counterpartyOf', {
                              companyName: companyName || ''
                            })}`
                          : ''
                      }
                    >
                      <li {...rest}>
                        {getCounterPartySearchOptionLabel(
                          option as CounterpartyDropDown
                        )}
                        {(option as CounterpartyDropDown)
                          .isCompanyCounterparty && (
                          <StarRateIcon
                            color="primary"
                            sx={{ ml: 1, mb: 0.5 }}
                          />
                        )}
                      </li>
                    </Tooltip>
                  );
                }}
              />
            </FormControl>
          );
        }}
      />
      {showCancel && !showCreateCard && (
        <Button variant="outlined" size="small" onClick={onCancel}>
          {translate('buttons.cancel')}
        </Button>
      )}
      {!notEnoughCharactersToSearch &&
        !isLoading &&
        options.length === 0 &&
        !showCreateCard && (
          <Box>
            <Typography
              sx={{
                color: '#9CA3AF',
                textAlign: 'left',
                mt: 0.5,
                fontSize: '0.875rem'
              }}
            >
              {translate('labels.noCounterpartiesFoundFor', {
                value: searchParams
              })}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              sx={{ mt: 1.5 }}
              onClick={() => onCreateClick(searchParams)}
            >
              {translate('buttons.createCounterparty')}
            </Button>
          </Box>
        )}
    </Box>
  );
};
